import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, isRef as _isRef, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("p", null, "Укажите точный адрес торгового объекта, либо введите ориентиры в комментарий.", -1)

import { Ref, computed, ref } from 'vue'
import {
  VPhoneField,
  VMunicipalEntitiesSelect,
  DeleteConfirmationDialog,
  AddressSuggester,
} from '../../../../components'
import {
  AppAuthService,
  MunicipalEntitiesService,
  SalesPoint,
  UpdateSalesPointOpts,
  Permissions,
  UsersService,
  AddressSuggesterService,
  AddressLevel,
  AddressSuggestion,
  SalesPointType,
  salesPointTypeName,
} from '@prodcard/core'
import { removeFieldsFromEntity, subjectOfRF, useWatcherSavedRef } from '../../../../helpers'
import useVuelidate, { ErrorObject } from '@vuelidate/core'
import { required, requiredIf, minValue, helpers, numeric, integer } from '@vuelidate/validators'
import { vMaska } from 'maska'
import { useAuthPermissions } from '../../../../mixins'
import { repeat } from 'lodash'
import { inn } from '../../../../common'

interface Props {
  municipalEntitiesService: MunicipalEntitiesService
  authService: AppAuthService
  usersService: UsersService
  addressSuggesterService: AddressSuggesterService
  salesPoint?: SalesPoint
  disabled?: boolean
  loading?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SalesPointsForm',
  props: {
    municipalEntitiesService: {},
    authService: {},
    usersService: {},
    addressSuggesterService: {},
    salesPoint: {},
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false }
  },
  emits: ['submit', 'delete'],
  setup(__props: any, { emit }) {

const props = __props;





const { currentMunicipalEntityId, hasPermissions } = useAuthPermissions(props.authService)

const manage = computed(() => {
  return hasPermissions(Permissions.SALES_POINTS_MANAGE)
})

const meManage = computed(() => {
  return !hasPermissions(Permissions.SALES_POINTS_MANAGE) && hasPermissions(Permissions.ME_SALES_POINTS_MANAGE)
})

const salesPointOpts: Ref<UpdateSalesPointOpts> = useWatcherSavedRef('SalesPointOpts', {
  ...removeFieldsFromEntity(props.salesPoint),
  municipalEntityId:
    meManage.value && !manage.value
      ? props.salesPoint?.municipalEntityId ?? currentMunicipalEntityId.value
      : props.salesPoint?.municipalEntityId,
})

const typeItems = Object.values(SalesPointType).map(s => {
  return {
    title: salesPointTypeName(s),
    value: s,
  }
})

const innMask = { mask: repeat('#', 12) }

const deleteConfirmation = ref(false)

const rules = computed(() => ({
  municipalEntityId: {
    required: helpers.withMessage('Муниципальное образование не может быть пустым', required),
  },
  locality: {
    required: helpers.withMessage('Населенный пункт не может быть пустым', required),
  },
  street: {
    requiredIf: helpers.withMessage(
      'Улица не может быть пустой',
      requiredIf(() => {
        return !salesPointOpts.value.comment
      }),
    ),
  },
  building: {
    requiredIf: helpers.withMessage(
      'Дом не может быть пустым',
      requiredIf(() => {
        return !salesPointOpts.value.comment
      }),
    ),
  },
  name: {
    required: helpers.withMessage('Наименование не может быть пустым', required),
  },
  inn: {
    required: helpers.withMessage('ИНН не может быть пустым', required),
    inn: helpers.withMessage('ИНН некорректен', inn),
    numeric: helpers.withMessage('ИНН может содержать только цифры', numeric),
  },
  type: {
    required: helpers.withMessage('Тип не может быть пустым', required),
  },
  maxPopulation: {
    required: helpers.withMessage('Возможности по прикреплению населения не могут быть пустыми', required),
    minValue: helpers.withMessage('Возможности по прикреплению населения не могут быть меньше 0', minValue(0)),
  },
  notificationMark: {
    required: helpers.withMessage('Отметка об уведомлении не может быть пустой', required),
  },
  comment: {
    requiredIf: helpers.withMessage(
      'Комментарий не может быть пустым',
      requiredIf(() => {
        return !salesPointOpts.value.street || !salesPointOpts.value.building
      }),
    ),
  },
  area: {
    required: helpers.withMessage('Торговая площадь не может быть пустой', required),
    minValue: helpers.withMessage('Торговая площадь не может быть меньше 0', minValue(0)),
    integer: helpers.withMessage('Значение не может быть дробным', integer),
  },
  phone: {
    phone: helpers.withMessage('Контактный телефон некорректен', () => {
      if (!salesPointOpts.value.phone) return true

      return salesPointOpts.value.phone.length === 11
    }),
  },
}))

const validator = useVuelidate<UpdateSalesPointOpts>(rules, salesPointOpts)

const submit = async () => {
  const valid = await validator.value.$validate()

  if (valid) emit('submit', salesPointOpts.value)
}

const getErrorMessages = (errors: ErrorObject[]) => {
  return errors.map((e: ErrorObject) => e.$message).join(', ')
}

const updateAddressWithSuggestion = (suggest: AddressSuggestion) => {
  if (suggest.municipalEntityId && suggest.municipalEntityId !== salesPointOpts.value.municipalEntityId) {
    salesPointOpts.value.municipalEntityId = suggest.municipalEntityId
  }
  if (
    suggest.address[AddressLevel.LOCALITY] &&
    suggest.address[AddressLevel.LOCALITY] !== salesPointOpts.value.locality
  ) {
    salesPointOpts.value.locality = suggest.address[AddressLevel.LOCALITY]
  }
  if (suggest.address[AddressLevel.STREET] && suggest.address[AddressLevel.STREET] !== salesPointOpts.value.street) {
    salesPointOpts.value.street = suggest.address[AddressLevel.STREET]
  }
  if (suggest.address[AddressLevel.HOUSE] && suggest.address[AddressLevel.HOUSE] !== salesPointOpts.value.building) {
    salesPointOpts.value.building = suggest.address[AddressLevel.HOUSE]
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(submit, ["prevent"]),
    disabled: _ctx.loading || _ctx.disabled
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(subjectOfRF),
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(subjectOfRF) ? (subjectOfRF).value = $event : null)),
                label: "Субъект РФ*",
                readonly: true
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VMunicipalEntitiesSelect), {
                modelValue: _unref(salesPointOpts).municipalEntityId,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(salesPointOpts).municipalEntityId) = $event)),
                "municipal-entities-service": _ctx.municipalEntitiesService,
                "error-messages": getErrorMessages(_unref(validator).municipalEntityId.$errors),
                disabled: _ctx.loading || _ctx.disabled,
                readonly: !manage.value && meManage.value,
                label: "Муниципальное образование*"
              }, null, 8, ["modelValue", "municipal-entities-service", "error-messages", "disabled", "readonly"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(AddressSuggester), {
                modelValue: _unref(salesPointOpts).locality,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(salesPointOpts).locality) = $event)),
                "address-suggester-service": _ctx.addressSuggesterService,
                "municipal-entity-id": _unref(salesPointOpts).municipalEntityId,
                level: _unref(AddressLevel).LOCALITY,
                "error-messages": getErrorMessages(_unref(validator).locality.$errors),
                label: "Населенный пункт*",
                "onUpdate:selectedValue": updateAddressWithSuggestion
              }, null, 8, ["modelValue", "address-suggester-service", "municipal-entity-id", "level", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _hoisted_1
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "8"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(AddressSuggester), {
                modelValue: _unref(salesPointOpts).street,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(salesPointOpts).street) = $event)),
                "address-suggester-service": _ctx.addressSuggesterService,
                "municipal-entity-id": _unref(salesPointOpts).municipalEntityId,
                "other-fields": { [_unref(AddressLevel).LOCALITY]: _unref(salesPointOpts).locality },
                level: _unref(AddressLevel).STREET,
                "error-messages": getErrorMessages(_unref(validator).street.$errors),
                label: "Улица*",
                "onUpdate:selectedValue": updateAddressWithSuggestion
              }, null, 8, ["modelValue", "address-suggester-service", "municipal-entity-id", "other-fields", "level", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "4"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(AddressSuggester), {
                modelValue: _unref(salesPointOpts).building,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(salesPointOpts).building) = $event)),
                "address-suggester-service": _ctx.addressSuggesterService,
                "municipal-entity-id": _unref(salesPointOpts).municipalEntityId,
                "other-fields": {
            [_unref(AddressLevel).LOCALITY]: _unref(salesPointOpts).locality,
            [_unref(AddressLevel).STREET]: _unref(salesPointOpts).street,
          },
                level: _unref(AddressLevel).HOUSE,
                "error-messages": getErrorMessages(_unref(validator).building.$errors),
                label: "Дом*",
                "onUpdate:selectedValue": updateAddressWithSuggestion
              }, null, 8, ["modelValue", "address-suggester-service", "municipal-entity-id", "other-fields", "level", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(salesPointOpts).name,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(salesPointOpts).name) = $event)),
                "error-messages": getErrorMessages(_unref(validator).name.$errors),
                type: "text",
                label: "Наименование хозяйствующего субъекта, осуществляющего торговую деятельность*"
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            lg: "6"
          }, {
            default: _withCtx(() => [
              _withDirectives(_createVNode(_component_v_text_field, {
                modelValue: _unref(salesPointOpts).inn,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(salesPointOpts).inn) = $event)),
                "error-messages": getErrorMessages(_unref(validator).inn.$errors),
                type: "text",
                label: "ИНН хозяйствующего субъекта*"
              }, null, 8, ["modelValue", "error-messages"]), [
                [_unref(vMaska), void 0, innMask]
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            lg: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_select, {
                modelValue: _unref(salesPointOpts).type,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(salesPointOpts).type) = $event)),
                items: _unref(typeItems),
                label: "Тип*",
                "no-data-text": "Нет данных",
                variant: "outlined",
                clearable: "",
                "error-messages": getErrorMessages(_unref(validator).type.$errors)
              }, null, 8, ["modelValue", "items", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            lg: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(salesPointOpts).maxPopulation,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(salesPointOpts).maxPopulation) = $event)),
                modelModifiers: { number: true },
                "error-messages": getErrorMessages(_unref(validator).maxPopulation.$errors),
                type: "number",
                label: "Возможности по прикреплению населения (тыс. чел.)*"
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            lg: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(salesPointOpts).notificationMark,
                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((_unref(salesPointOpts).notificationMark) = $event)),
                "error-messages": getErrorMessages(_unref(validator).notificationMark.$errors),
                type: "text",
                label: "Отметка об уведомлении*"
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_textarea, {
                modelValue: _unref(salesPointOpts).comment,
                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((_unref(salesPointOpts).comment) = $event)),
                "error-messages": getErrorMessages(_unref(validator).comment.$errors),
                label: "Комментарий",
                "auto-grow": ""
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "8"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(salesPointOpts).openingHours,
                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((_unref(salesPointOpts).openingHours) = $event)),
                label: "Режим работы"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "4"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(salesPointOpts).area,
                "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((_unref(salesPointOpts).area) = $event)),
                modelModifiers: { number: true },
                "error-messages": getErrorMessages(_unref(validator).area.$errors),
                type: "number",
                label: "Торговая площадь (кв. м)*"
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: 6
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VPhoneField), {
                phone: _unref(salesPointOpts).phone,
                "onUpdate:phone": _cache[13] || (_cache[13] = ($event: any) => ((_unref(salesPointOpts).phone) = $event)),
                "error-messages": getErrorMessages(_unref(validator).phone.$errors),
                label: "Контактный телефон"
              }, null, 8, ["phone", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(salesPointOpts).responsiblePersonName,
                "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((_unref(salesPointOpts).responsiblePersonName) = $event)),
                label: "Ответственное лицо"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, { justify: "end" }, {
        default: _withCtx(() => [
          (_ctx.salesPoint && (manage.value || meManage.value))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_v_col, { cols: "auto" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      color: "red-accent-4",
                      variant: "outlined",
                      loading: _ctx.loading,
                      disabled: _ctx.disabled,
                      onClick: _cache[15] || (_cache[15] = ($event: any) => (deleteConfirmation.value = true))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Удалить ")
                      ]),
                      _: 1
                    }, 8, ["loading", "disabled"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DeleteConfirmationDialog), {
                  modelValue: deleteConfirmation.value,
                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((deleteConfirmation).value = $event)),
                  onAgree: _cache[17] || (_cache[17] = ($event: any) => (emit('delete', _ctx.salesPoint.id)))
                }, {
                  title: _withCtx(() => [
                    _createTextVNode(" Подтвердите действие ")
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ], 64))
            : _createCommentVNode("", true),
          _createVNode(_component_v_col, { cols: "auto" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                type: "submit",
                color: "primary",
                loading: _ctx.loading,
                disabled: _ctx.disabled
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Сохранить ")
                ]),
                _: 1
              }, 8, ["loading", "disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["onSubmit", "disabled"]))
}
}

})